import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Paths that must NEVER show the launcher (public / pre-auth pages). */
function isPublicPath(pathname) {
  if (!pathname) return true;
  if (pathname === '/' ) return true;
  if (pathname.startsWith('/auth')) return true;
  return false;
}

/**
 * FeedbackLauncher — persistent bottom-right ⚠ Report Issue pill for
 * authenticated users. Mounts globally inside <BrowserRouter>. Hides on
 * public/auth paths and when the user is not authenticated.
 *
 * - One /api/auth/me check on first authenticated path; cached after.
 * - Modal submits to POST /api/feedback/report.
 * - Resend/internal errors are never surfaced to the user.
 */
function FeedbackLauncher() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auth gating: never render on public paths; otherwise hydrate from
  // /api/auth/me. We deliberately do NOT use a ref-based "only fetch
  // once" gate here — under React 18 StrictMode the first effect's
  // cleanup cancels the in-flight axios.get and the second effect would
  // see the ref already set and skip, leaving `user` permanently null on
  // any full-reload route (/dashboard accidentally worked because that
  // path was reached via SPA navigation, which fires the effect only
  // once). A cheap GET on each effect re-run (only when the
  // public/private category changes) is correct and simple.
  const onPublicPath = isPublicPath(location.pathname);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (onPublicPath) {
        if (!cancelled) setUser(null);
        return;
      }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) setUser(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [onPublicPath]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/feedback/report`,
        {
          type: 'issue_report',
          message: (message || '').trim(),
          blocked,
          page_path: window.location.pathname,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          source_context: 'global_report_issue_button',
        },
        { withCredentials: true }
      );
      toast.success("Thanks. We'll review this personally.");
      setOpen(false);
      setMessage('');
      setBlocked(false);
    } catch (_err) {
      // Never expose raw backend errors.
      toast.error("We couldn't send that just now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [message, blocked]);

  if (isPublicPath(location.pathname) || !user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="report-issue-launcher"
        aria-label="Report an issue"
        className="fixed z-50 bottom-4 right-4 sm:bottom-5 sm:right-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-white text-amber-800 border border-amber-300 shadow-md hover:bg-amber-50 hover:border-amber-400 transition-colors print:hidden"
      >
        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" aria-hidden="true" />
        <span>Report Issue</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!submitting) setOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle>Report an issue</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {(user?.name || user?.email) && (
              <div className="text-xs text-slate-500" data-testid="report-issue-user">
                {user.name ? <span className="font-medium text-slate-700">{user.name}</span> : null}
                {user.name && user.email ? ' · ' : ''}
                {user.email || null}
              </div>
            )}

            <div className="text-xs text-slate-500" data-testid="report-issue-page">
              Page: <span className="text-slate-700">{window.location.pathname}</span>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="report-issue-message">
                Tell us what happened
              </label>
              <Textarea
                id="report-issue-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Briefly describe what you were doing and what went wrong."
                rows={5}
                disabled={submitting}
                data-testid="report-issue-textarea"
              />
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
              <input
                type="checkbox"
                checked={blocked}
                onChange={(e) => setBlocked(e.target.checked)}
                disabled={submitting}
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                data-testid="report-issue-blocked"
              />
              This blocked me
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="report-issue-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="report-issue-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeedbackLauncher;
